import { Grid } from '@mui/material';

import React, { useCallback, useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { ITabValues } from 'common/api/type-def';
import ButtonComp from 'common/components/Button/ButtonComp';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import TabBar from 'common/components/TabBar/TabBar';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { PAGINATION_CONSTANTS } from 'common/constants';
import { DATA_ACCESS_PERMISSIONS_ACTIONS } from 'common/constants/PermissionsConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setPagination,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import {
	setRolePermissionDrawer,
	setGroupPermissionDrawer,
	setRoleDetails,
	setGroupDetails,
} from 'common/store/reducer/permissionsReducer';
import { setPermissionsTabIndex } from 'common/store/reducer/userManagementReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { getGroupsTableData, getRolesTableData } from './api';
import {
	IGroups,
	IGroupsApiResponse,
	IRole,
	IRolesApiResponse,
} from './api/type-def';
import CreateGroup from './CreateGroups/CreateGroup';
import GroupsTable from './Groups/GroupsTableCmp';
import CreateRole from './Roles/CreateRole';
import RolesTable from './Roles/RolesTable';

import styles from './Permission.module.scss';

interface TabBarProps {
	details: ITabValues[];
	components: { id: number; children: React.JSX.Element }[];
	showButton: boolean;
	showCount: boolean;
	onTabChange: (index: number) => void;
	testId?: string;
}

const { CREATELABEL } = DATA_ACCESS_PERMISSIONS_ACTIONS;

const Permission = () => {
	const dispatch = useDispatch();

	const rolePermissionDrawer = useSelector(
		(state: RootState) => state.permissionsmodule.permissionRoleDrawer,
	);
	const selectedRoleDetails = useSelector(
		(state: RootState) => state.permissionsmodule.roleDetails,
	);
	const selectedGroupDetails = useSelector(
		(state: RootState) => state.permissionsmodule.groupDetails,
	);

	const openGroupDrawer = useSelector(
		(state: RootState) => state.permissionsmodule.permissionGroupDrawer,
	);

	const paramsData = useSelector((state: RootState) => state.common.pagination);

	const [rolesData, setRolesData] = useState<IRole[]>([]);
	const [groupsData, setGroupsData] = useState<IGroups[]>([]);
	const [, setError] = useState<boolean>(false);
	const [isLoading, setLoading] = useState<boolean>(false);
	const [activeTab, setActiveTab] = useState(0);
	const [totalPagesCount, setTotalPagesCount] = useState(0);
	const [totalRecordsCount, setTotalRecordCount] = useState<number>(0);
	const [buttonTestId, setbuttonTestId] = useState('');
	const [buttonText, setbuttonText] = useState('');

	const { message, severity, isToastOpen } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({ message: '', severity: '', isToastOpen: false }),
		);
	}, []);

	const fetchRolesTableData = async (data: any) => {
		setError(false);
		setLoading(true);
		setRolesData([]);
		try {
			const response = await getRolesTableData(data);
			if (response?.responseBody) {
				const responseData: IRolesApiResponse = response.responseBody;
				let tempTableData: IRole[] = responseData.content;
				tempTableData = tempTableData.map((role: IRole) => ({
					...role,
					id: role.roleId,
				}));
				setRolesData(tempTableData);
				setTotalRecordCount(responseData.totalCount);
				setTotalPagesCount(responseData.totalPages);
				setLoading(false);
			}
		} catch (error) {
			setError(true);
			setToastMessage({
				message: APP_GENERAL_ERROR_MSG,
				severity: 'error',
				isToastOpen: true,
			});
			setLoading(false);
		}
	};

	const fetchGroupsTableData = async (data: any) => {
		setError(false);
		setLoading(true);
		setGroupsData([]);
		try {
			const response = await getGroupsTableData(data);
			if (response?.responseBody) {
				const responseData: IGroupsApiResponse = response.responseBody;
				let tempTableData: IGroups[] = responseData.content;
				tempTableData = tempTableData.map((group: IGroups) => ({
					...group,
					id: group.groupId,
				}));
				setGroupsData(tempTableData);
				setTotalRecordCount(responseData.totalCount);
				setTotalPagesCount(responseData.totalPages);
				setLoading(false);
			}
		} catch (error) {
			setError(true);
			setToastMessage({
				message: APP_GENERAL_ERROR_MSG,
				severity: 'error',
				isToastOpen: true,
			});
			setLoading(false);
		}
	};

	const openCreateRoleDrawer = useCallback(() => {
		dispatch(
			setRoleDetails({
				type: 'Create',
				data: {
					roleId: '',
					roleDesc: '',
					roleName: '',
					users: [],
					dataPermissions: [],
					modulePermissions: [],
				},
			}),
		);
		dispatch(setRolePermissionDrawer(true));
	}, []);
	const openCreateGroupDrawer = useCallback(() => {
		dispatch(setGroupDetails({ type: 'Create', data: {} }));
		dispatch(setGroupPermissionDrawer(true));
	}, []);

	const createPermission = () => {
		if (activeTab === 0) {
			openCreateRoleDrawer();
		} else if (activeTab === 1) {
			openCreateGroupDrawer();
		}
	};

	useEffect(() => {
		if (activeTab === 1) {
			fetchGroupsTableData(paramsData);
		} else {
			fetchRolesTableData(paramsData);
		}
	}, [paramsData]);

	useEffect(() => {
		if (!rolePermissionDrawer) {
			if (activeTab === 0) {
				if (selectedRoleDetails.type === '') {
					fetchRolesTableData(paramsData);
				} else {
					const sortParamsObj = sortParamsData(selectedRoleDetails.type);
					fetchRolesTableData(sortParamsObj);
					dispatch(setRoleDetails({ type: '', data: {} }));
				}
			}
		}
		if (!openGroupDrawer) {
			if (activeTab === 1) {
				const sortParamsObj = sortParamsData(selectedGroupDetails.type);
				fetchGroupsTableData(sortParamsObj);
				dispatch(setGroupDetails({ type: '', data: {} }));
			}
		}
	}, [rolePermissionDrawer, openGroupDrawer]);

	const sortParamsData = (type: string) => {
		const buildPayLoadObject = {
			sortKey: type === CREATELABEL ? 'createdDate' : 'modifiedDate',
			pageSize: paramsData.pageSize,
			pageNumber: paramsData.pageNumber,
			sortDirection: 'desc',
		};

		return buildPayLoadObject;
	};

	const tabs = [
		{
			id: 1,
			children: (
				<RolesTable
					rolesData={rolesData}
					isLoading={isLoading}
					fetchRolesTableData={fetchRolesTableData}
					totalRecordsCount={totalRecordsCount}
					totalPagesCount={totalPagesCount}
				/>
			),
		},
		{
			id: 2,
			children: (
				<GroupsTable
					groupsData={groupsData}
					isLoading={isLoading}
					fetchGroupsTableData={fetchGroupsTableData}
					totalRecordsCount={totalRecordsCount}
					totalPagesCount={totalPagesCount}
				/>
			),
		},
	];

	const PermissiontabDetails: ITabValues[] = [
		{
			name: 'Roles',
			count: '',
		},
		{
			name: 'Groups',
			count: '',
		},
	];

	useEffect(() => {
		if (activeTab === 0) {
			setbuttonTestId('create-Role');
			setbuttonText('Role');
		} else if (activeTab === 1) {
			setbuttonTestId('create-Group');
			setbuttonText('Group');
		}
	}, [activeTab]);

	const tabBarProps: TabBarProps = {
		details: PermissiontabDetails,
		components: tabs,
		showButton: false,
		showCount: false,
		testId: 'tab-list',
		onTabChange: (activeTab) => {
			dispatch(
				setPagination({
					sortKey: '',
					pageSize: PAGINATION_CONSTANTS.PAGE_SIZE,
					pageNumber: PAGINATION_CONSTANTS.PAGE_NUMBER,
					sortDirection: '',
				}),
			);
			setActiveTab(activeTab);
			dispatch(setPermissionsTabIndex(activeTab));
		},
	};

	return (
		<Grid container data-testid="table-area">
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<TabBar
					{...tabBarProps}
					showButton
					childComponent={
						<ButtonComp
							btnType="primary"
							size="small"
							data-testid={buttonTestId}
							clickHandler={createPermission}
							compClass={styles['create-btns']}
						>
							{toSentenceCase(`Create ${buttonText}`)}
						</ButtonComp>
					}
					activeTab={activeTab}
				/>
			</Grid>

			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
			{rolePermissionDrawer && (
				<SideDrawer
					className={styles['create-roles-drawer']}
					drawerState={rolePermissionDrawer}
				>
					<CreateRole />
				</SideDrawer>
			)}
			{openGroupDrawer && (
				<SideDrawer
					className={styles['create-roles-drawer']}
					drawerState={openGroupDrawer}
				>
					<CreateGroup />
				</SideDrawer>
			)}
		</Grid>
	);
};

export default Permission;
